import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Link } from '@mui/material';
import { FlexCol, FlexCenVer } from '@/UI/styles';
import { useSettingsStore } from '@/state/settings.state';
import useWorkspacesStore from '@/state/workspaces.state';
import { I18N_KEYS } from '@/language/keys';
import { TextLgMd, TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { AppSwitch } from '@/UI/AppSwitch/AppSwitch';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_AUTO_APPROVE_VERIFIED_MODELS } from '@/consts/featureFlags';
import { useUpdateAutoApprove } from '@/queries/useModelManagement';
import { ModelTable } from './ModelsTable/ModelsTable';

export const ModelManagement = () => {
  const { t } = useTranslation();
  const workspaceModels = useSettingsStore((state) => state.workspaceModels);
  const workspaceModelsLoadingState = useSettingsStore((state) => state.workspaceModelsLoadingState);
  const activeWorkspace = useWorkspacesStore((state) => state.activeWorkspace);
  const shouldBlockUnverifiedModels = Boolean(activeWorkspace.preferences?.shouldBlockUnverifiedModels);
  const shouldAutoApproveVerifiedModels = activeWorkspace.preferences?.shouldAutoApproveVerifiedModels ?? false;
  const { value: isAutoApproveFFEnabled } = useFeatureFlag(FF_AUTO_APPROVE_VERIFIED_MODELS);

  const [isReadMoreExpanded, setIsReadMoreExpanded] = useState(false);

  const isLoading = workspaceModelsLoadingState === 'loading';

  const { mutate: updateAutoApprove } = useUpdateAutoApprove();

  const handleAutoApproveToggle = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      updateAutoApprove(checked);
    },
    [updateAutoApprove],
  );

  const toggleReadMore = useCallback(() => {
    setIsReadMoreExpanded((prev) => !prev);
  }, []);

  return (
    <FlexCol sx={{ flex: 1, height: '100%', gap: 4, pb: 3 }}>
      <FlexCol sx={{ gap: 2, pt: '1.5px' }}>
        <TextLgMd text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TITLE)} />
        <TextSmRg color={color.White64_T}>
          {t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.DESCRIPTION_SHORT)}{' '}
          <Link
            component="button"
            underline="always"
            onClick={toggleReadMore}
            sx={{ color: color.White64_T, verticalAlign: 'baseline', fontSize: 'inherit', lineHeight: 'inherit' }}
          >
            {isReadMoreExpanded
              ? t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.READ_LESS)
              : t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.READ_MORE)}
          </Link>
        </TextSmRg>
        {isReadMoreExpanded && (
          <FlexCol sx={{ gap: 2 }}>
            <FlexCol sx={{ gap: 0.5 }}>
              <TextSmMd text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.SUBTITLE_VERIFIED)} color={color.White80_T} />
              <TextSmRg text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TEXT_VERIFIED)} color={color.White64_T} />
            </FlexCol>
            {!shouldBlockUnverifiedModels && (
              <FlexCol sx={{ gap: 0.5 }}>
                <TextSmMd text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.SUBTITLE_UNVERIFIED)} color={color.White80_T} />
                <TextSmRg text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.TEXT_UNVERIFIED)} color={color.White64_T} />
              </FlexCol>
            )}
          </FlexCol>
        )}
      </FlexCol>

      {isAutoApproveFFEnabled && (
        <>
          <Divider />
          <FlexCenVer sx={{ justifyContent: 'space-between', gap: 3 }}>
            <FlexCol sx={{ gap: 0.5 }}>
              <TextSmMd text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.AUTO_APPROVE_VERIFIED.LABEL)} />
              <TextSmRg
                text={t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.AUTO_APPROVE_VERIFIED.DESCRIPTION)}
                color={color.White64_T}
              />
            </FlexCol>
            <AppSwitch
              checked={shouldAutoApproveVerifiedModels}
              onChange={handleAutoApproveToggle}
              inputProps={{ 'aria-label': 'auto-approve verified models' }}
            />
          </FlexCenVer>
          <Divider />
        </>
      )}

      <ModelTable models={workspaceModels} isLoading={isLoading} />
    </FlexCol>
  );
};
